'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import { getAudioCoordinator } from '@/lib/audio-coordinator'

const COORDINATOR_ID = 'voice-sample'

/**
 * One <audio> element at a time for the voice sample rows. Nothing is requested
 * until a visitor presses Play on a row, and starting a sample stops every
 * other player through the audio coordinator.
 */
export function useVoiceSamplePlayer() {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [playingId, setPlayingId] = useState<string | null>(null)
  const [failedId, setFailedId] = useState<string | null>(null)

  const stop = useCallback(() => {
    audioRef.current?.pause()
    setPlayingId(null)
  }, [])

  useEffect(() => {
    const unsubscribe = getAudioCoordinator().subscribe(COORDINATOR_ID, stop)
    return () => {
      unsubscribe()
      audioRef.current?.pause()
    }
  }, [stop])

  const toggle = useCallback((voiceId: string, src: string) => {
    const coordinator = getAudioCoordinator()
    if (playingId === voiceId) {
      stop()
      coordinator.releasePlayback(COORDINATOR_ID)
      return
    }

    coordinator.requestPlayback(COORDINATOR_ID)
    setFailedId(null)
    audioRef.current?.pause()
    const audio = new Audio()
    const fail = () => {
      setPlayingId(null)
      setFailedId(voiceId)
      coordinator.releasePlayback(COORDINATOR_ID)
    }
    audio.onended = () => {
      setPlayingId(null)
      coordinator.releasePlayback(COORDINATOR_ID)
    }
    audio.onerror = fail
    // audio-egress-allow: visitor pressed Play on a voice sample
    audio.src = src
    audioRef.current = audio
    // Pausing before playback has started rejects play() with an AbortError;
    // that is the visitor stopping the sample, not a load failure.
    audio.play().catch((error: unknown) => {
      if (!(error instanceof DOMException && error.name === 'AbortError')) fail()
    })
    setPlayingId(voiceId)
  }, [playingId, stop])

  return { playingId, failedId, toggle, stop }
}
